import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialShareAccessDeniedStep from 'containers/SocialShareModal/SocialShareAccessDeniedStep';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useThreadsAuthenticator from 'hooks/useThreadsAuthenticator';
import {
  scheduleThreadsVideo,
  shareThreadsVideo,
} from 'redux/modules/download';
import { threadsSelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import { useThreadsPostComposerStep } from './steps';
import { ThreadsSocialShareStep } from './types';

export default function useThreadsSocialShare(): SocialSharePlatformData<
  ThreadsSocialShareStep
> {
  const { providerUserId, profileImageUrl, username, postStatus } = useSelector(
    threadsSelector,
  );

  const dispatch = useDispatch<Dispatch>();

  const {
    description,
    onAuthSuccess,
    onAuthError,
    goToStep,
  } = useSocialShareModal();

  const { authenticating, authenticate } = useThreadsAuthenticator({
    force: true,
    onAuthSuccess: () => {
      onAuthSuccess();
      goToStep('composer');
    },
    onError: error => {
      onAuthError(error);
      goToStep('access-denied');
    },
  });

  const handleShare = useOnShare({
    shareNow: useCallback(() => dispatch(shareThreadsVideo(description)), [
      description,
      dispatch,
    ]),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(scheduleThreadsVideo(description, scheduledDate)),
      [description, dispatch],
    ),
  });

  const threadsAccessDeniedStep = useSocialShareAccessDeniedStep({
    onCancel: () => goToStep('composer'),
  });

  const threadsPostComposerStep = useThreadsPostComposerStep({
    onShare: handleShare,
  });

  return {
    platformAlias: 'threads',
    authenticating: authenticating || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [threadsAccessDeniedStep, threadsPostComposerStep],
    defaultStep: threadsPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
